import { BaseModel, ID } from '@state/base';
import { ColumnLike } from '@component/collection-table';
import { Entity, EntityMemberCommunicationType, EntityRole, LocaleConfig } from '@state/entities';
import { EntityType } from '@component/entity-form-modal';
import { FeatureRoleConfigurationActionType } from '@state/entity';
import { Holdings } from '@component/add-holdings';
import { InputGridRow } from '@component/input-grid';
import { OverviewCard } from '@component/overview-cards';

export interface AddMembersContext {
  newMembers: InputGridRow[];
  source: AddMembersOrigin;
  // any matches typing for router.navigate
  returnUrlSegments: any[];
}

export enum AddMembersOrigin {
  DataRoom = 'data room'
}

export enum OwnershipClassName {
  EntityRelationship = 'EntityRelationship',
  Investment = 'Investment',
  Holding = 'Holding'
}

export enum OwnershipType {
  Investments = 'investments',
  Holdings = 'holdings', // eslint-disable-line @typescript-eslint/no-shadow
  Entities = 'entities'
}

export const ownershipTypeLabel = {
  [OwnershipType.Investments]: 'investors',
  [OwnershipType.Holdings]: 'holdings',
  [OwnershipType.Entities]: 'entities'
};

export enum PropertyType {
  Boolean = 'boolean',
  Currency = 'currency',
  Date = 'date',
  String = 'string',
  Integer = 'numeric'
}

type FormTitle = string;

export interface InvestorQuestionnaireData {
  completed_at?: Date;
  title: string;
  form_data: Record<FormTitle, FormFieldData[]>;
}

export interface InvestorQuestionnaireReview {
  action: ID;
  activities: QuestionnaireReviewActivity[];
}

export type InvestorQuestionnaireReviewActivityLog = QuestionnaireReviewActivity[];
export interface QuestionnaireReviewActivity extends BaseModel {
  action_type: FeatureRoleConfigurationActionType;
  author: Author;
  data: QuestionnaireReviewActivityData;
  note?: string;
}

interface Author {
  email: string;
  first_name: string;
  full_name: string;
  id: ID;
  last_name: string;
  middle_name?: string;
  name: string;
}

interface QuestionnaireReviewActivityData {
  message: string;
}

export interface FormFieldData {
  field: FormField;
  meta_hash: MetaHash;
}

export interface CustomValue {
  [custom_variable_key: string]: string;
}

interface FormField {
  custom_variable_key: string;
  id: ID;
  label: string;
}
interface MetaHash {
  key: string;
  obfuscated: boolean;
  title?: string;
  type: string;
  value?: string;
}

export interface LegalColumn {
  cv_key: string;
  tooltip?: string;
  type: PropertyType;
  value: string;
}

export interface LegalData {
  columns: LegalColumn[];
  rows: LegalRow[];
}

// TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
export interface LegalSection {
  id: ID;
  name: string;
}

// TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
export interface LegalSectionData {
  columns: ColumnLike[];
  dataSource: LegalRow[];
}

// TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
export enum LegalStatus {
  Approved = 'approved',
  Incomplete = 'incomplete',
  ReadyForReview = 'ready_for_review',
  UpdateRequired = 'investor_update_required'
}

// TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
export interface LegalRow {
  investor: string;
  investment_id: string;
  investor_email: string;
  signature_request_state: LegalStatus;
  state_updated_at: string;
}
export interface BlueSkyRow {
  investors_count: number;
  state: string;
  total_contributed_amount: string;
}
export interface BlueSky {
  results: BlueSkyRow[];
  totals: {
    total_contributed_amount: string;
    total_investors: number;
  };
}

export interface InvestorPayload {
  carry_percent?: string;
  city?: string;
  contact_email?: string;
  contact_name?: string;
  contact_phone_number?: string;
  contact_title?: string;
  country?: string;
  customer_code?: string;
  email?: string;
  entity_subtype?: string;
  entity_type?: string;
  first_name?: string;
  investor_type?: string;
  joint_email?: string;
  joint_legal_name?: string;
  last_name?: string;
  legal_name?: string;
  line_one?: string;
  line_two?: string;
  management_fee_percent?: string;
  member_1_email?: string;
  member_1_name?: string;
  member_2_email?: string;
  member_2_name?: string;
  member_3_email?: string;
  member_3_name?: string;
  name?: string;
  ownership_units?: number | string;
  postal?: string;
  security_share_price?: number | string;
  staged?: boolean;
  state?: string;
  subscription_amount?: number | string;
  tax_id_type?: string;
  tax_id?: string;
  total_received_amount?: number | string;
  trust_subtype?: string;
  trust_type?: string;
}

export interface InviteRequestOptions {
  bcc_emails?: string[];
  cc_emails?: string[];
  from?: string;
  investors: InvestorPayload[];
  message?: string;
  notify_team?: boolean;
  send_message: boolean;
  subject?: string;
}

export interface LPProfileDataRow extends BaseModel {
  accr_affiliated_investors?: string;
  accr_investment_company_act?: string;
  accr_investment_company_act_humanized?: string;
  accr_investor_rep_non_us_fatca: boolean;
  accredited?: boolean;
  address_city?: string;
  address_country?: string;
  address_line_one?: string;
  address_line_two?: string;
  address_postal?: string;
  address_state?: string;
  bank_account?: string;
  bank_address?: string;
  bank_beneficiary_address?: string;
  bank_name?: string;
  bank_routing?: string;
  bank_swift?: string;
  carry_percent?: string;
  carry_percent_formatted?: string;
  commitment_amount?: string;
  contact_email?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_title?: string;
  contributed_amount?: number;
  date_of_formation?: string;
  entity_subtype?: string;
  entity_type?: string;
  erisa_dol?: string;
  erisa_dol_humanized?: string;
  formation_country?: string;
  formation_state?: string;
  investor_email?: string;
  investor_name?: string;
  is_domestic?: boolean;
  is_us_person?: boolean;
  joint_email?: string;
  joint_legal_name?: string;
  legal_name?: string;
  management_fee_percent?: string;
  management_fee_percent_formatted?: string;
  qualified_client?: boolean;
  qualified_purchaser?: boolean;
  qualified_purchaser_2?: boolean;
  subscription_amount?: string;
  tax_id?: string;
  tax_id_type?: string;
  total_received?: string;
  trust_subtype?: string;
  trust_type?: string;
  unfunded_commitment_amount?: string;
  wire_beneficiary?: string;
  wire_instructions?: string;
}

export interface LPProfileData {
  results: LPProfileDataRow[];
}

export interface OwnershipOption {
  class_name: OwnershipClassName;
  id: ID;
  meta: string;
  value: string;
}

export interface CSVExportResponse extends BaseModel {
  thumbnail?: string;
  title: string;
}

export interface Fund extends BaseModel {
  allow_cap_call_events: boolean;
  allow_lawyers_review: boolean;
  calculation_method: string;
  capital_due_date?: string;
  context: {
    id: ID;
    logo?: string;
    name: string;
    organization_entity_id: ID;
  };
  entity_id: ID;
  execute_by?: string;
  has_subscription_agreement?: boolean;
  investment_count: number;
  investors_locked: boolean;
  is_managed?: boolean;
  suspended?: boolean;
  legal_name: string;
  locale_config?: LocaleConfig;
  logo: string;
  manual_accounting: boolean;
  minimum_investment: number;
  name: string;
  organization_id: ID;
  organization_logo?: string;
  organization_name: string;
  organization_url_slug: string;
  ownership_units_enabled: boolean;
  portfolio_company_legal_name: string;
  round_type: string;
  security: string;
  security_share_price: number;
  size: number;
  summary?: string;
  total_contributed: number;
  total_contributed_formatted: string;
  total_invested: number;
  total_invested_formatted?: string;
  totals: {
    target_size: string;
    total_allocations: string;
    total_commitments: string;
    total_contributed: string;
    total_invited: number;
  };
  type: string;
  vehicle_id?: ID;
  vintage_year?: number;
}

export interface FundBankAccount {
  available_balance_formatted: string;
  bank_name: string;
  current_balance_formatted: string;
  id: ID;
  last_checked_at: string;
  last_checked_at_formatted: string;
  last_error_at: string;
  last_error_message: string;
  mask: string;
  name: string;
  total_transactions: number;
}

export interface Activity {
  action: string;
  created_at_in_words: string;
  id: ID;
  initials: string;
}

export interface Action {
  cta: string;
  type: 'api' | 'application';
  url: string;
}

export interface Actionable {
  label: string;
  logo?: string;
}

export interface TaskActor {
  initials: string;
  name: string;
}

export interface Task {
  action: Action;
  actionable: Actionable;
  actor: TaskActor;
  message: string;
}

export interface Summary {
  id: ID;
  tasks: Task[];
  activities: Activity[];
  committed: string;
  contributed: string;
  target: string;
  target_size: string;
  total_committed: string;
  total_contributed?: string;
  total_contributed_formatted_short?: string;
  total_contributed_formatted?: string;
  total_life_distributions?: string;
  total_net_asset_value?: string;
  total_unfunded_commitment_amount?: string;
}

// Row shape returned by GET /v1/vehicles/:id/investors/fetch_investments — see
// flowapi/app/services/v1/vehicles/export_investments.rb. Currency/percent fields
// are pre-formatted, locale-aware strings; status is encoded as flat booleans
// rather than a nested state object.
export interface Investor {
  // Identifiers
  investment_id: ID;
  investment_account_id: ID;
  investment_url: string;

  // Names / contact
  display_name: string;
  legal_name: string;
  investor_email: string;
  // Server-composed full name (first + middle + last when LP user exists).
  investor_name: string;

  // Account / accreditation
  account_type: string;
  accreditation_type: string;

  // Pre-formatted financials
  carry_percent: string;
  contributed_amount: string;
  management_fee_percent: string;
  net_asset_value: string;
  ownership_percent: string;
  ownership_price_per_unit: string;
  ownership_units: number | string;
  proceeds: string;
  received_amount: string;
  subscribed: string;
  unfunded_amount: string;

  // Status / substate booleans. The three optional fields are null on the
  // wire (rendered as undefined to TS) when the related feature
  // (questionnaire review, wire-instructions task) is disabled on the
  // vehicle — distinct from "false" which means the feature is on but the
  // substate isn't active.
  bank_wire_instructions?: boolean;
  completed: boolean;
  contributed: boolean;
  // `*_step_present` booleans mirror the legacy `state.investment_substates`
  // array — true when the workflow created that substate on the active state.
  // The icon series gates each entry on the matching flag so we don't render
  // an icon for a step that isn't part of this investment's workflow. Distinct
  // from the corresponding ended booleans (`contributed`, `documents_signed`,
  // `countersigned`, `profile_selected`), which report completion of a step
  // that exists.
  contributed_step_present: boolean;
  countersigned: boolean;
  countersigned_step_present: boolean;
  documents_signed: boolean;
  signed_step_present: boolean;
  has_countersigner_role: boolean;
  invitation_delivered: boolean;
  invitation_opened: boolean;
  invitation_sent: boolean;
  pending_lp_update?: boolean;
  pending_review?: boolean;
  profile_selected: boolean;
  profile_step_present: boolean;
  side_letter: boolean;
  viewed: boolean;

  // Metadata
  last_updated: string;
}

export interface InvestorTotals {
  contributed_amount: string;
  net_asset_value: string;
  ownership_units: string | null;
  proceeds: string;
  received_amount: string;
  subscribed: string;
  unfunded_amount: string;
}

export interface FetchInvestmentsResponse {
  ownership_units_enabled: boolean;
  questionnaire_review: boolean;
  rows: Investor[];
}

export interface InvestorsTotalsResponse {
  totals: InvestorTotals;
}

export interface UnverifiedPrimaryContactsResponse {
  unverified_investment_ids: ID[];
}

// Aggregates the Trigger Countersigning modal needs once a selection is in
// hand — computed server-side so the modal opens without per-LP fetches.
export interface CountersigningSummary {
  max_counter_role_count: number;
  any_unsigned_primary: boolean;
  any_pending_countersign: boolean;
  any_already_countersigned: boolean;
}

export interface CarryRecipient extends BaseModel {
  display_name: string;
  entity_relationship_id: string;
  formation_date: string;
  legal_name: string;
  notes: string;
  roles: string[];
  tax_id: string;
  type: string;
  type_formatted: string;
  vehicle_id: ID;
}

export interface ManagementFeeRecipient extends BaseModel {
  display_name: string;
  entity_relationship_id: ID;
  formation_date: string;
  legal_name: string;
  notes: string;
  roles: string[];
  tax_id: ID;
  type: string;
  type_formatted: string;
  vehicle_id: ID;
}

export interface Details extends BaseModel {
  accounting_method_humanized: string;
  arbitration_location: string;
  audit_required: boolean;
  auditor_email: string;
  auditor_name: string;
  auditor_phone: string;
  capital_due_date?: string;
  carried_interest: string;
  carried_interest_formatted: string;
  carry_percent: string;
  carry_recipient: CarryRecipient;
  close_on: string;
  complex_carry: string;
  complex_carry_details: string;
  complex_carry_humanized: string;
  entity: Entity;
  execute_by: string;
  expense_reserve: number;
  expense_reserve_formatted: string;
  external_auditor: boolean;
  fee: string;
  general_partners?: Partner[];
  form_id_cik: string;
  id: ID;
  investment?: string;
  is_managed?: boolean;
  management_companies: Partner[];
  management_fee: string;
  management_fee_formatted: string;
  management_fee_percent: string;
  management_fee_recipient: ManagementFeeRecipient;
  management_fee_term: string;
  minimum_investment: number;
  minimum_investment_formatted: string;
  ownership_units_enabled: boolean;
  portfolio_company_logo: string;
  registered_agent: string;
  reporting_frequency: string;
  reporting_frequency_humanized: string;
  security_share_price: number;
  security_share_price_formatted: string;
  size: number;
  summary: string;
  target_formatted: string;
  total_fund_contribution_formatted: string;
  vintage_year?: number;
}

export interface Holding {
  company_address: string;
  company_contact_email: string;
  company_contact_fax: string;
  company_contact_name: string;
  company_contact_phone: string;
  company_contact_title: string;
  company_date_of_formation: string;
  company_ein: string;
  company_entity_type: string;
  company_formation_country: string;
  company_logo: string;
  company_name: string;
  created_at: string;
  created_at_formatted: string;
  holding_security: string;
  id: ID;
  post_valuation_formatted: string;
  security_share_price: string;
  security_share_price_formatted: string;
  security_share_total_formatted: string;
  size: number;
  size_formatted: string;
  summary: string;
  total_investment_formatted: string;
  updated_at: string;
  updated_at_formatted: string;
  updates: any[];
}

export interface Partner {
  created_at_formatted: string;
  created_at_millis: number;
  created_at: string;
  display_name?: string;
  entity_relationship_id: string;
  formation_date?: string;
  id: ID;
  legal_name: string;
  notes: string;
  roles: EntityRole[];
  tax_id?: string;
  type_formatted: string;
  type: EntityType;
  updated_at_formatted: string;
  updated_at_millis: number;
  updated_at: string;
  vehicle_id: ID;
}

export interface FundNotification {
  action: string;
  label: string;
  subscribed: boolean;
}

export interface FundNotifications {
  columnSettings: ColumnLike[];
  dataSource: FundNotification[];
}

export interface FundState {
  activeLegalStatusTags?: LegalStatus[];
  details: Details;
  fund?: Fund;
  holding: Holding;
  holdings?: Holdings[];
  investorFormData?: InvestorQuestionnaireData[];
  investorDataReview?: InvestorQuestionnaireReview;
  investors: Investor[];
  legalData?: LegalData;
  legalSections?: LegalSection[];
  loading: boolean;
  notifications?: FundNotification[];
  overviewInvestments: OverviewCard[][];
  selectedLegalSection?: LegalSection;
  summary: Summary;
}
export interface QuestionnaireReviewMessage {
  bcc_emails?: string[];
  cc_emails?: string[];
  from?: string;
  message: string;
  reply_to?: string;
  subject: string;
  communication_types: EntityMemberCommunicationType[];
}
